import { lazyComponentLoader, retry } from '@/helpers/lazyComponentLoader'
import Vue from 'vue'
import { captureException } from '@sentry/vue'
import goldkidApi from '@/api/goldkid/goldkidApi'

let times = 0
const maxTimes = 30
const timeout = 3000

const state = () => {
  return {
    userData: {},
    kidData: {},
    kidInfo: {},
    pId: '',
    meta: {},
    frontSettings: {
      isGuideTipsEnabled: false
    },
    errorText: "",
    component: 'start',
    allComponents: {
      error: {
        name: 'error',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/error/error')), true)
      },
      start: {
        name: 'start',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/landing/container')), true)
      },
      goToCardomat: {
        name: 'goToCardomat',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/go-to-cardomat/go-to-cardomat')), true)
      },
      reject: {
        name: 'reject',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/reject/reject')), true)
      },
      otp: {
        name: 'otp',
        loader: lazyComponentLoader(retry(() => import('@/components/Otp/Otp')), true)
      },
      Unavailable: {
        name: 'Unavailable',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/unavailable/unavailable')), true)
      },
      MainAgreement: {
        name: 'MainAgreement',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/main-agreement/main-agreement')), true)
      },
      GoldkidAgreement: {
        name: 'GoldkidAgreement',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/goldkid-agreement/goldkid-agreement')), true)
      },
      kidList: {
        name: 'kidList',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/kid-list/kid-list')), true)
      },
      noKidFound: {
        name: 'noKidFound',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/no-kid-found/no-kid-found')), true)
      },
      reRelease: {
        name: 'reRelease',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/re-release/re-release')), true)
      },
      enterPhone: {
        name: 'enterPhone',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/enter-phone/enter-phone')), true)
      },
      debts: {
        name: 'debts',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/debts/debts')), true)
      },
      newYearUnavailable: {
        name: 'newYearUnavailable',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/new-year-unavailable/new-year-unavailable')), true)
      },
      photoVer: {
        name: 'photoVer',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/photo-verification/photo-verification')), true)
      },
      systemError: {
        name: 'systemError',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/error/error.vue')), true)
      },
      reEnterPhone: {
        name: 'reEnterPhone',
        loader: lazyComponentLoader(retry(() => import('@/components/pages/reenter-phone/reenter-phone.vue')), true)
      }
    }
  }
}

const getters = {
  meta: state => state.meta,
  component: state => state.component,
  userData: state => state.userData,
  kidData: state => state.kidData,
  kidInfo: state => state.kidInfo,
  pId: state => state.pId,
  isGuideTipsEnabled: state => state.frontSettings.isGuideTipsEnabled,
  currentDate: state => ({
    currentDateRu: state.userData?.currentDateRu || '',
    currentDateKz: state.userData?.currentDateKz || ''
  }),
  getErrorText: state => state.errorText
}

const mutations = {
  setKidData: (state, data) => {
    state.kidData = data
  },
  setKidInfo: (state, data) => {
    state.kidInfo = data
  },
  meta: (state, meta) => {
    state.meta = meta
  },
  component: (state, value) => {
    state.component = value
  },
  setUserData: (state, value) => {
    state.userData = value
  },
  setSettings: (state, payload) => {
    if (payload) {
      state.frontSettings = payload
    }
  },
  pId: (state, pId) => {
    state.pId = pId
  },
  setErrorText: (state, error) => {
    state.errorText = error
  }
}

const actions = {
  setErrorText: function ({ commit }, errorText) {
    commit('setErrorText', errorText)
  },
  component: async function ({ commit, state }, component) {
    try {
      const { name, loader } = state.allComponents[component]
      if (!Vue.options.components[name]) {
        Vue.component(name, loader)
      }

      commit('component', name)
    } catch (err) {
      captureException(err)

      commit('component', 'error')
      // window.location.href = process.env.VUE_APP_ERROR_PAGE
    }
  },
  pId: async function ({ commit }, pId) {
    commit('pId', pId)
  },
  getUserProfile: async function (state) {
    return goldkidApi.getUserProfile().then((response) => {
      state.commit('setUserData', response?.data?.data)

      return response
    })
  },
  dataStep: async function ({ commit, state }) {
    return goldkidApi.dataStep(state.meta.processId)
      .then((response) => {
        if (response?.data?.meta) {
          commit('meta', response?.data?.meta)
        }
        return response
      })
  },
  processStep: async function ({ commit, getters, dispatch }, data) {
    return goldkidApi.processStep(data)
      .then((response) => {
        if (response?.data?.meta) {
          commit('meta', response?.data?.meta)
        }

        return response
      })
      .then(response => {
        if (response?.data?.result === 'WAIT') {
          return dispatch('status')
        }

        return response
      })
  },
  async logEventToKafka({ state, dispatch }, event) {
    return await goldkidApi.logEventToKafka(event)
  },
  status: async function ({ state, dispatch, commit }) {
    const response = await goldkidApi.status(state.meta.processId)

    times += 1

    if (response?.data?.result && response?.data?.result !== 'WAIT') {
      return response
    }

    if (times >= maxTimes) {
      commit('component', 'error')
    } else {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(dispatch('status'))
        }, timeout)
      })
    }
  },
  async getSettings({ commit, state }) {
    try {
      const response = await goldkidApi.getSettings()
      if (response?.data) {
        commit('setSettings', response.data)
      }
    } catch {
      //
    }
  }
}
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
